import CloseRounded from '@mui/icons-material/CloseRounded';
import Box, { type BoxProps } from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { AnimatePresence, m } from 'motion/react';
import { type KeyboardEvent } from 'react';

import {
  AppButton,
  NUDGE_SHEEN_DELAY_MS,
  nudgeDetailItemMotion,
  nudgeDetailMotion,
  nudgeSlideMotion,
  Sheen,
  WhatsAppButton,
} from '@/components/atoms';
import { tipChrome } from '@/content/tips';
import { useTipCarousel } from '@/hooks/useTipCarousel';
import { type ResolvedPageTip } from '@/utils/pageTip';

import { TipDeckControls } from './TipDeckControls';
import { TipGlyph } from './TipGlyph';

/**
 * `aria-labelledby` targets. There is only ever one card on screen, and —
 * because the slide transition runs in `wait` mode — only ever one slide
 * mounted inside it, so these ids stay unique as the deck turns. That is not
 * a happy accident: with overlapping slides the dialog would briefly have two
 * elements answering to its label, and a screen reader would read whichever
 * it found first.
 */
const TITLE_ID = 'kbx-tip-title';
const BODY_ID = 'kbx-tip-body';

/**
 * `ref` is taken from `BoxProps` and passed on by spreading rather than being
 * declared here as `Ref<HTMLDivElement>`. Under `exactOptionalPropertyTypes` an
 * own optional `ref` widens to include `undefined`, which MUI's own prop type
 * does not accept when handed over explicitly — the same reason `GlassPanel`
 * takes the whole of `BoxProps`. The ref is focused only when a tap asked for
 * this card; see `ScrollNudge`.
 */
type Props = Pick<BoxProps, 'ref'> & {
  tip: ResolvedPageTip;
  onDismiss: () => void;
  /** The visitor took the tip up — pressed the CTA or WhatsApp. */
  onEngage: () => void;
};

/**
 * The tip itself: a page's deck of three messages, one at a time, and a way
 * out.
 *
 * ## Solid, and no longer glass
 *
 * This card was a `GlassPanel` on the `theme` tint, chosen so it would stay
 * legible over a photograph, a pattern or flat paper — which it did, by taking
 * its colour from whatever it was floating over. That is the correct choice
 * for a panel that belongs to a band and the wrong one for a panel that has to
 * announce itself: a surface engineered to agree with its surroundings cannot
 * also stand out from them, and the frosted rim read as one more piece of
 * furniture on a site that already has a translucent header, a promise ribbon
 * and two bottom bars.
 *
 * So: opaque paper, a real shadow, and a brand-red edge down the left side.
 * The edge is the cheapest possible accent — four pixels, no new colour
 * decisions, nothing for the text's contrast to be measured against — and it
 * is the mark the collapsed pill carries too, so the two read as one object.
 *
 * It is a `borderLeft` rather than a pseudo-element on purpose. `Sheen` below
 * paints its own `::before` across the full interior, and a pseudo-element
 * edge would have to win a stacking argument against it on every frame of the
 * pass; a border is painted outside the padding box, where nothing inside the
 * card can reach it.
 *
 * ## What is in the deck, and what stays still while it turns
 *
 * Every page carries three messages rather than one (`content/tips.ts`), so
 * the card has to be two things at once: a dialog that arrived, and a carousel
 * inside it. The division is in the markup and it is the whole layout:
 *
 * - The **glyph and the close button** are the card's own furniture. They sit
 *   outside the rotating region and never move, because the one thing a
 *   visitor must be able to rely on while content changes underneath them is
 *   where the exit is.
 * - The **eyebrow, headline, body and buttons** are the slide. All four change
 *   together and travel together, which is what makes the change read as a
 *   new message rather than as a panel rearranging itself.
 * - The **dots and the pause button** are the deck's controls. They stay put
 *   like the furniture, but they belong to the carousel region so that
 *   reaching them with the keyboard suspends the rotation.
 *
 * ## Equal height by construction
 *
 * The headline and the body reserve two and three lines respectively whether
 * they use them or not, and the action row is always exactly one row —
 * educative slides have no page CTA and show the WhatsApp seat alone rather
 * than dropping the row. So every slide in a deck is the same height, and the
 * card's top edge does not jump as the deck turns. (It is anchored at the
 * bottom, so any growth would push the top edge up — directly under the
 * reader's eye, which is the worst place for it.)
 *
 * Measuring the slides and locking the card to the tallest would be the other
 * way to do it, and it is worse: it costs a layout pass per deck, it cannot
 * run before the first paint, and it reserves space for a worst case the copy
 * can simply be written to avoid. The same reasoning as the vehicle cards.
 *
 * ## The motion, in three passes
 *
 * The box is dropped onto the launcher by `nudgeCardMotion` — a short fall,
 * two decaying bounces, settled. Its contents then land as a short cascade
 * (`nudgeDetailMotion`) — the furniture, then the deck — so the card reads as
 * composing itself rather than as a slab that faded up.
 *
 * It is two beats and not one per line, which it used to be, and the reason is
 * structural rather than aesthetic. The slide is now a `motion` element with
 * its own `initial`/`animate` objects, and an element that states those does
 * not pass a variant label down to its children — so per-line cascade
 * wrappers inside the slide would have sat there inert, waiting for a label
 * that never arrived. They belong to the slide now, and the slide moves as one
 * block. That is the right answer anyway: five lines cascading *and* sliding
 * on every turn of the deck is two animations out of phase, which at this size
 * reads as a glitch — the same reason the card's contents do not bounce with
 * the box.
 *
 * Then, once everything is still,
 * a single `Sheen` pass crosses it; that atom exists for exactly this and is
 * already used on the band headlines.
 *
 * Every slide change after that is the third pass and a much quieter one
 * (`nudgeSlideMotion`): a 24px shift and a crossfade, in `wait` mode so two
 * copies of the body copy are never on screen together. The card itself does
 * not move again — it arrived once.
 *
 * ## Non-modal, and what that costs
 *
 * `role="dialog"` with no `aria-modal`, no focus trap, and no focus move when
 * it arrives on its own. The card does not take focus unasked because the
 * visitor is in the middle of reading something — moving the caret to an
 * uninvited message is the keyboard equivalent of the thing this is trying not
 * to be. It is in the DOM, reachable by Tab, and Escape closes it from
 * anywhere on the page.
 *
 * The card is **not** an `aria-live` region, which is the one genuine
 * trade-off here. A live region would announce it as it arrives — but a status
 * message containing interactive controls is what live regions are documented
 * not to be: the announcement interrupts whatever is being read, and the
 * buttons it just described are nowhere near the user's focus. The accepted
 * pattern for a notification with controls in it is a dialog that persists and
 * waits, which is what this is.
 *
 * The *rotating region* inside it does carry `aria-live`, and it is `off`
 * while the deck is turning by itself — see `useTipCarousel`.
 *
 * ## No timer on the card
 *
 * The deck turns, but the card never auto-dismisses. Content a visitor is
 * expected to read, that removes itself on a clock, is a WCAG 2.2.1 failure
 * unless it can be paused or extended — and a tip that vanishes mid-sentence
 * is a worse advert for the company than no tip at all. It goes when the
 * visitor says so, and what it leaves behind is the launcher.
 */
export function TipCard({ tip, onDismiss, onEngage, ...rest }: Props) {
  const deck = useTipCarousel(tip.slides);
  const { slide } = deck;

  /**
   * Left and right step through the deck, which is what the carousel pattern
   * asks for and the only keyboard route to a slide that has gone past other
   * than tabbing to the dots.
   *
   * It is bound to the **card root** and not to the carousel region it steers,
   * which is the one thing about it that is easy to get wrong. Focus lands on
   * the root — that is the node `ScrollNudge` focuses when a visitor opens the
   * card from the launcher, and it is the node carrying the `tabIndex` — and
   * React events bubble up, not down. A handler on the region would therefore
   * never see a keypress from the visitor it was written for, and would only
   * fire once they had already tabbed into the dots. On the root it catches
   * both: its own keypresses, and everything bubbling out of the region.
   *
   * `preventDefault` because both keys otherwise scroll the page horizontally,
   * and the card is focused the moment a visitor opens it from the launcher —
   * so without it the first arrow press would move the page under a card the
   * visitor was trying to read. Only these two keys are claimed: Escape is
   * handled on `document` by the hook precisely so it works when focus was
   * never in here, and Tab must keep doing what Tab does.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    if (event.key === 'ArrowRight') deck.next();
    else deck.previous();
  };

  return (
    <Box
      {...rest}
      tabIndex={-1}
      role="dialog"
      onKeyDown={onKeyDown}
      aria-labelledby={TITLE_ID}
      aria-describedby={BODY_ID}
      sx={{
        overflow: 'hidden',
        borderRadius: 4,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderLeft: 4,
        borderLeftColor: 'primary.main',
        // Higher than the launcher's 6 and than the pill's 8: this is the
        // nearest object to the reader and the shadow is what says so.
        boxShadow: 12,
        outline: 'none',
      }}
    >
      {/* `Sheen` carries the padding, so its pass covers the whole interior
          rather than a box inset inside it. Light crossing only the text block
          with a 24px unlit frame around it reads as a highlight on the words;
          crossing the surface reads as the card catching the light, which is
          the gesture. */}
      <Sheen play delayMs={NUDGE_SHEEN_DELAY_MS} sx={{ p: 6 }}>
        <m.div {...nudgeDetailMotion}>
          <Stack spacing={3}>
            {/* The card's furniture: the mark it shares with the disc and the
                pill, and the way out. Outside the rotating region, because
                while the content changes under them these two are the only
                things the visitor can count on staying where they were. */}
            <m.div {...nudgeDetailItemMotion}>
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <TipGlyph size={32} />
                <Box sx={{ flex: 1 }} />
                {/* No size override, which is the point: the theme floors
                    every icon button at the 44px touch target, so this one is
                    already right. `ImportPromiseRibbon` has to override that
                    *down* because it is squeezed into a 40px bar; a card with
                    room to spare has no excuse. Pulled out of the padding so
                    the glyph lines up with the text rather than with its own
                    tap area. */}
                <IconButton
                  onClick={onDismiss}
                  aria-label={tipChrome.dismiss}
                  sx={{ flexShrink: 0, mt: -2, mr: -2, color: 'text.secondary' }}
                >
                  <CloseRounded fontSize="small" />
                </IconButton>
              </Stack>
            </m.div>

            {/* The carousel. A `section` with `aria-roledescription`, which is
                the WAI-ARIA pattern: the role stays `region` (so the label is
                honoured and the landmark is real) and the description tells a
                screen reader what kind of region it is. `suspendProps` is here
                rather than on the card so that the rotation stops for a
                pointer resting on the words or a keyboard reaching the dots,
                and keeps running for a pointer that merely crossed the
                corner. */}
            <m.div {...nudgeDetailItemMotion}>
              <Box
                component="section"
                aria-roledescription="carousel"
                aria-label={tipChrome.carousel}
                aria-live={deck.live}
                {...deck.suspendProps}
              >
                <Stack spacing={3}>
                  {/* `wait`, and not the sync mode the stage swap upstairs uses.
                    Two slides crossing inside a 360px card is unreadable, and
                    `aria-labelledby` points at one headline id — which would
                    be ambiguous with two of them mounted. */}
                  <AnimatePresence mode="wait" initial={false}>
                    <m.div
                      key={deck.index}
                      {...nudgeSlideMotion(deck.direction)}
                      role="group"
                      aria-roledescription="slide"
                      aria-label={tipChrome.slidePosition(slide.eyebrow, deck.index, deck.total)}
                    >
                      <Stack spacing={3}>
                        <Typography variant="overline" sx={{ color: 'primary.main' }}>
                          {slide.eyebrow}
                        </Typography>

                        {/* Two lines reserved whether this headline uses them
                            or not. See "equal height by construction" above. */}
                        <Typography
                          id={TITLE_ID}
                          variant="h5"
                          component="h2"
                          sx={{
                            minHeight: (t) => `calc(${t.typography.h5.lineHeight} * 2em)`,
                          }}
                        >
                          {slide.title}
                        </Typography>

                        {/* Three, which is what ~25 words comes to at this
                            width. The copy in `content/tips.ts` is written to
                            it. */}
                        <Typography
                          id={BODY_ID}
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            minHeight: (t) => `calc(${t.typography.body2.lineHeight} * 3em)`,
                          }}
                        >
                          {slide.body}
                        </Typography>

                        {/* Both actions retire the tip to the launcher: it has
                          been read, and a visitor who comes back to this page
                          does not need telling twice. The difference is what
                          each one means for the *next* tip — see `engage` in
                          `useScrollNudge`.

                          An educative slide has no `cta` and shows WhatsApp
                          alone. The row itself always renders, so the card
                          does not change height when the deck turns onto one —
                          and the visitor is never left a slide with no way to
                          act on it, which would be the one slide in three that
                          quietly wasted its turn. */}
                        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2}>
                          {slide.cta && (
                            <AppButton to={slide.cta.to} size="small" onClick={onEngage}>
                              {slide.cta.label}
                            </AppButton>
                          )}
                          <WhatsAppButton
                            message={slide.whatsappMessage}
                            size="small"
                            onClick={onEngage}
                          >
                            WhatsApp
                          </WhatsAppButton>
                        </Stack>
                      </Stack>
                    </m.div>
                  </AnimatePresence>

                  <TipDeckControls
                    index={deck.index}
                    total={deck.total}
                    paused={deck.paused}
                    onGoTo={deck.goTo}
                    onTogglePaused={deck.togglePaused}
                  />
                </Stack>
              </Box>
            </m.div>
          </Stack>
        </m.div>
      </Sheen>
    </Box>
  );
}
